import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  RESPIRATORY_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Mild Asthma Study
 * 
 * Inclusion criteria:
 * - Age >=18
 * - Asthma diagnosis >12 months ago
 * - No inhaled therapy or low dose ICS/low dose ICS+LABA
 * - BMI <=40
 */
export const mildAsthmaScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "asthma_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with asthma?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I have been diagnosed with asthma by a healthcare provider " },
      { 
        value: "no", 
        label: "No, I have not been diagnosed with asthma by a healthcare provider",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for people who have been diagnosed with asthma.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "asthma_diagnosis_timing",
    type: QuestionType.RADIO,
    question: "When was your asthma first diagnosed?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "within_12_months", 
        label: "I was diagnosed within the past 12 months",
        disqualify: true,
      },
      { value: "more_than_12_months", label: "I was diagnosed more than 12 months ago" },
    ],
    disqualifyMessage:
      "This study requires participants to have had asthma for more than 12 months.",
    attributeMapping: mappers.value({
      within_12_months: [{ attributeDefinitionId: 23808, booleanAttributeValue: false }], // has_asthma_diagnosis_12_months
      more_than_12_months: [{ attributeDefinitionId: 23808, booleanAttributeValue: true }],
    }),
  },
  {
    id: "asthma_medications",
    type: QuestionType.RADIO,
    question: "In terms of asthma medications, which option best describes what you use?",
    required: true,
    allowBack: true,
    options: [
      { value: "no_inhaled", label: "I don't use any puffers or inhalers at the moment" },
      { value: "reliever_only", label: "I use a rescue/reliever inhaler (such as Ventolin, ProAir or Proventil) when I need it" },
      { value: "reliever_and_preventer", label: "I use a reliever when I need it, along with a controller inhaler containing an inhaled corticosteroid every day (such as Flovent, Pulmicort, QVAR, Advair or Symbicort)" },
      { 
        value: "none_applies", 
        label: "None of these options apply to me",
        disqualify: true,
      },
      { value: "unsure", label: "I'm unsure about my asthma medications" },
    ],
    disqualifyMessage:
      "This study is only for people on low dose or no inhaled corticosteroids.",
    helpText: "If unsure about your medications, please consult your doctor or pharmacist",
    // TODO: Add attribute mapping when medication usage attribute is available
    // Currently no attribute for "asthma_medication_usage" pattern
    attributeMapping: mappers.value({
      no_inhaled: [],
      reliever_only: [],
      reliever_and_preventer_low: [],
      none_applies: [],
      unsure: [],
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    maxBMI: 40,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },
  {
    id: "asthma_severity_statements",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you regarding your asthma?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "life_threatening_5_years",
        label: "I have had a life threatening asthma attack in the last 5 years",
        disqualify: true,
      },
      {
        value: "hospitalized_12_months",
        label: "I have been hospitalized for my asthma in the past 12 months",
        disqualify: true,
      },
      {
        value: "systemic_steroids_6_months",
        label: "I have needed a course of systemic steroid medication (tablets or injections) for my asthma or another medical condition in the past 6 months",
        disqualify: true,
      },
      { value: "none", label: "None of the above are true for me" },
    ],
    disqualifyMessage:
      "This study cannot include people with recent severe asthma exacerbations or recent systemic steroid use.",
    attributeMapping: mappers.value({
      life_threatening_5_years: [
        { attributeDefinitionId: 24201, booleanAttributeValue: true }, // has_asthma_lifethreatening_event_5_years
      ],
      hospitalized_12_months: [
        { attributeDefinitionId: 23824, booleanAttributeValue: true }, // has_asthma_exacerbation_severe_12_months
      ],
      systemic_steroids_6_months: [
        { attributeDefinitionId: 25360, booleanAttributeValue: true }, // has_asthma_exacerbation_severe_6_months_hospital_or_steroids
      ],
      none: [
        { attributeDefinitionId: 24201, booleanAttributeValue: false },
        { attributeDefinitionId: 23824, booleanAttributeValue: false },
        { attributeDefinitionId: 25360, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "lung_conditions",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "copd",
        label: "I have COPD",
        disqualify: true,
      },
      {
        value: "interstitial_lung",
        label: "I have interstitial lung disease or lung fibrosis",
        disqualify: true,
      },
      {
        value: "alpha1",
        label: "I have alpha 1 antitrypsin deficiency",
        disqualify: true,
      },
      {
        value: "lung_cancer",
        label: "I have lung cancer",
        disqualify: true,
      },
      {
        value: "cystic_fibrosis",
        label: "I have cystic fibrosis",
        disqualify: true,
      },
      {
        value: "bronchiectasis",
        label: "I have bronchiectasis",
        disqualify: true,
      },
      { value: "none", label: "None of the above are true for me" },
    ],
    disqualifyMessage:
      "This study is specifically for asthma and cannot include people with other serious lung conditions.",
    attributeMapping: mappers.value({
      copd: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true }],
      interstitial_lung: [{ attributeDefinitionId: 25661, booleanAttributeValue: true }], // has_ILD_prerequisite
      alpha1: [{ attributeDefinitionId: 25637, booleanAttributeValue: true }], // has_a1ad_prerequisite
      lung_cancer: [{ attributeDefinitionId: 20810, booleanAttributeValue: true }], // has_lungcancer
      cystic_fibrosis: [{ attributeDefinitionId: 25767, booleanAttributeValue: true }], // has_CF_prerequisite
      bronchiectasis: [{ attributeDefinitionId: 25949, booleanAttributeValue: true }], // has_bronchiectasis_prerequisite
      none: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: false },
        { attributeDefinitionId: 25661, booleanAttributeValue: false },
        { attributeDefinitionId: 25637, booleanAttributeValue: false },
        { attributeDefinitionId: 20810, booleanAttributeValue: false }, // has_lungcancer
        { attributeDefinitionId: 25767, booleanAttributeValue: false },
        { attributeDefinitionId: 25949, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "serious_conditions",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "end_stage_kidney",
        label: "I have end stage kidney disease or I am currently on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "addisons",
        label: "I have Addison's disease",
        disqualify: true,
      },
      {
        value: "cushings",
        label: "I have Cushing's syndrome",
        disqualify: true,
      },
      { value: "none", label: "None of the above are true for me" },
    ],
    disqualifyMessage:
      "This study cannot include people with these serious medical conditions.",
    attributeMapping: mappers.value({
      end_stage_kidney: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: 25770, booleanAttributeValue: true }, // has_dialysis_current
      ],
      liver_failure: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
      ],
      severe_heart_failure: [
        { attributeDefinitionId: 25631, booleanAttributeValue: true }, // has_heartfailure_prerequisite
      ],
      addisons: [
        { attributeDefinitionId: 25740, booleanAttributeValue: true }, // has_addisons_prerequisite
      ],
      cushings: [
        { attributeDefinitionId: 25676, booleanAttributeValue: true }, // has_cushings_ever
      ],
      none: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: 25770, booleanAttributeValue: false }, // has_dialysis_current
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
        { attributeDefinitionId: 25631, booleanAttributeValue: false },
        { attributeDefinitionId: 25740, booleanAttributeValue: false },
        { attributeDefinitionId: 25676, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "other_conditions",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pregnant_breastfeeding",
        label: "I am pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "coronary_heart",
        label: "I have coronary heart disease",
        disqualify: true,
      },
      {
        value: "aortic_aneurysm",
        label: "I have been diagnosed with an abdominal aortic aneurysm (AAA)",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "I have had a cancer diagnosis or cancer treatment within the past 5 years (excluding skin cancers such as SCC and BCC)",
        disqualify: true,
      },
      {
        value: "mental_health_hospitalized",
        label: "I have been hospitalized for a mental health condition within the past 12 months",
        disqualify: true,
      },
      { value: "none", label: "None of the above are true for me" },
    ],
    disqualifyMessage:
      "This study cannot include people with these conditions.",
    attributeMapping: mappers.value({
      pregnant_breastfeeding: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
      ],
      coronary_heart: [
        { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease
      ],
      aortic_aneurysm: [
        { attributeDefinitionId: 21994, booleanAttributeValue: true }, // has_AAA
      ],
      cancer_5_years: [
        { attributeDefinitionId: 25304, booleanAttributeValue: true }, // has_cancer_5_years
      ],
      mental_health_hospitalized: [
        { attributeDefinitionId: 24003, booleanAttributeValue: true }, // has_mental_health_hospitalized_12_months
      ],
      none: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
        { attributeDefinitionId: 3436, booleanAttributeValue: false }, // has_coronaryarterydisease
        { attributeDefinitionId: 21994, booleanAttributeValue: false },
        { attributeDefinitionId: 25304, booleanAttributeValue: false }, // has_cancer_5_years
        { attributeDefinitionId: 24003, booleanAttributeValue: false }, // has_mental_health_hospitalized_12_months
      ],
    }),
  },
  {
    id: "smoking_history",
    type: QuestionType.RADIO,
    question: "Regarding your smoking history, select the option that best reflects your situation:",
    required: true,
    allowBack: true,
    options: [
      { value: "never_smoked", label: "I have never smoked cigarettes, cannabis or used vapes on a regular basis" },
      { value: "quit_not_heavy", label: "I quit smoking or vaping more than 6 months ago and was NOT a heavy smoker" },
      {
        value: "quit_heavy",
        label: "I quit smoking or vaping more than 6 months ago and WAS a heavy smoker",
        disqualify: true,
      },
      {
        value: "current_smoker",
        label: "I currently smoke cigarettes, cannabis or use vapes, or have done so regularly within the past 6 months",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study cannot include current smokers or people with a heavy smoking history.",
    // TODO: Add attributes for:
    // - Cannabis use (only has had_cannabis_ever)
    // - Vaping/e-cigarette use (no attributes found)
    // - Heavy smoking definition (no pack-years or heavy smoker boolean found)
    attributeMapping: mappers.value({
      never_smoked: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false }, // is_smoker
        { attributeDefinitionId: 20561, booleanAttributeValue: false }, // is_pastsmoker
        { attributeDefinitionId: 25772, booleanAttributeValue: false }, // had_cannabis_6_months
      ],
      quit_not_heavy: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: true }, // quit_smoking_6_months
        { attributeDefinitionId: 25772, booleanAttributeValue: false }, // had_cannabis_6_months
      ],
      quit_heavy: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: true },
        { attributeDefinitionId: 25772, booleanAttributeValue: false }, // had_cannabis_6_months
      ],
      current_smoker: [
        { attributeDefinitionId: 20917, booleanAttributeValue: true },
        { attributeDefinitionId: 25772, booleanAttributeValue: true }, // had_cannabis_6_months
      ],
    }),
  },
  {
    id: "other_studies",
    type: QuestionType.RADIO,
    question: "Are you currently participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am in another clinical trial",
        disqualify: true,
      },
      { value: "no", label: "No, I am not in any other clinical trials" },
    ],
    disqualifyMessage:
      "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
    }),
  },
  {
    id: "willing_to_attend",
    type: QuestionType.RADIO,
    question: "Are you willing to attend study visits at a research site?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I am willing to attend up to 5 visits over a 15 week period" },
      {
        value: "no",
        label: "No, I cannot commit to this appointment schedule",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss this schedule further with the research team",
      },
    ],
    disqualifyMessage:
      "This study requires attendance at in-person visits.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
      no: [{ attributeDefinitionId: 23479, booleanAttributeValue: false }],
      discuss: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
    }),
  },
];